import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FolderTree, Plus } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import type { Category } from '@/lib/types'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, EmptyState, Modal, Toggle } from '@/components/ui/primitives'
import { t } from '@/i18n'

export function CategoriesSection() {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<Category | null>(null)
  const [isOpen, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [colorHex, setColorHex] = useState('#0EA5A4')
  const [isActive, setIsActive] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiClient.get<Category[]>('/api/categories'),
  })

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        parentCategoryId: null,
        colorHex,
        sortOrder: editing?.sortOrder ?? 0,
        isActive,
      }
      return editing
        ? apiClient.put(`/api/categories/${editing.id}`, body)
        : apiClient.post('/api/categories', body)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['categories'] })
      setOpen(false)
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not save.'),
  })

  function open(category: Category | null) {
    setEditing(category)
    setName(category?.name ?? '')
    setColorHex(category?.colorHex ?? '#0EA5A4')
    setIsActive(category?.isActive ?? true)
    setError(null)
    setOpen(true)
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-[var(--text-secondary)]">
          {t('Categories group the touch grid at checkout and break down every sales report.')}
        </p>
        <Button size="sm" onClick={() => open(null)}>
          <Plus className="h-4 w-4" />
          {t('New')}
        </Button>
      </div>

      {categories.data?.length === 0 ? (
        <EmptyState
          icon={FolderTree}
          title={t('No categories yet')}
          description={t('Every product needs one. Start with the aisles you actually have.')}
          action={
            <Button onClick={() => open(null)}>
              <Plus className="h-4 w-4" />
              {t('New category')}
            </Button>
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {categories.data?.map((category) => (
            <li key={category.id}>
              <button
                type="button"
                onClick={() => open(category)}
                className="surface-raised flex w-full items-center gap-3 rounded-[var(--radius-card)] border p-3 text-left hover:border-[var(--accent)]"
              >
                <span
                  aria-hidden
                  className="h-8 w-8 shrink-0 rounded-lg"
                  style={{ backgroundColor: category.colorHex ?? 'var(--surface-sunken)' }}
                />
                <span className="min-w-0 flex-1 truncate font-medium">{category.name}</span>
                {!category.isActive && <Badge>{t('Inactive')}</Badge>}
                <span className="tabular shrink-0 text-sm text-[var(--text-secondary)]">
                  {category.productCount} products
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={isOpen}
        onClose={() => setOpen(false)}
        title={editing ? `Edit ${editing.name}` : 'New category'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending || !name.trim()}>
              {t('Save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Input
            label={t('Name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('Dairy')}
            required
          />
          <div>
            <label htmlFor="category-colour" className="text-sm font-medium">
              {t('Tile colour')}
            </label>
            <div className="mt-1.5 flex items-center gap-3">
              <input
                id="category-colour"
                type="color"
                value={colorHex}
                onChange={(e) => setColorHex(e.target.value)}
                className="h-11 w-16 cursor-pointer rounded-lg border border-[var(--border-subtle)] bg-transparent"
              />
              <span className="tabular text-sm text-[var(--text-secondary)]">{colorHex}</span>
            </div>
          </div>
          <Toggle checked={isActive} onChange={setIsActive} label={t('Active')} />
          {error && (
            <p role="alert" className="rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
              {error}
            </p>
          )}
        </div>
      </Modal>
    </div>
  )
}
